import type { CabinetDeviceSessionView } from "@planerius/contracts/cabinet-auth"

/**
 * Профиль и безопасность (1-Э1.10 администратора, 5-М7 мастера) как данные.
 *
 * Тексты — из листа «Мастер, доступ и заглушка — П1». Язык интерфейса, второй фактор,
 * сеанс поддержки и блок уведомлений в этот пакет не входят (П1-06): для них нет ни
 * i18n, ни заведения второго фактора, ни доставки уведомлений персоналу.
 */

export const TEXT_PASSWORD_CONSEQUENCE =
  "Смена закроет сеансы на других устройствах и на сутки закроет выгрузку базы."
export const TEXT_REVOKE_OTHERS_NOTE =
  "Телефон теряется и остаётся у бывшего сотрудника — а в нём расписание. Это единственный экран, где вы закроете доступ сами."
export const TEXT_OFFLINE =
  "Нет связи. Изменить пароль и завершить сеансы сейчас нельзя — повторите, когда связь вернётся."

/** «ИВ» из «Ирина Волкова»; одно слово — первая буква. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter((part) => part.length > 0)
  const letters = parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase())
  return letters.join("") || "?"
}

/**
 * Устройство словами: «iPhone · Safari», «Windows · Chrome». Подпись, которую человек дал
 * при входе, важнее разбора строки браузера: он узнаёт СВОЁ название («ноутбук на стойке»).
 */
export function deviceName(session: Pick<CabinetDeviceSessionView, "deviceLabel" | "userAgent">): string {
  if (session.deviceLabel !== null && session.deviceLabel.trim().length > 0) return session.deviceLabel.trim()
  const ua = session.userAgent ?? ""
  const os =
    /iPhone/.test(ua) ? "iPhone"
    : /iPad/.test(ua) ? "iPad"
    : /Android/.test(ua) ? "Android"
    : /Windows/.test(ua) ? "Windows"
    : /Mac OS X|Macintosh/.test(ua) ? "Mac"
    : /Linux/.test(ua) ? "Linux"
    : null
  const browser =
    /YaBrowser/.test(ua) ? "Яндекс Браузер"
    : /Edg\//.test(ua) ? "Edge"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari"
    : /curl\//.test(ua) ? "curl"
    : null
  if (os === null && browser === null) return "Неизвестное устройство"
  return [os, browser].filter((part) => part !== null).join(" · ")
}

/**
 * «14 сентября, 20:10» в часовом поясе того, кто смотрит. Дата и время собираются из двух
 * форматов: общий формат ru-RU ставит между ними «в», а на листе — запятая.
 */
export function formatMoment(instant: string | null, timeZone?: string): string {
  if (instant === null) return "—"
  const date = new Date(instant)
  if (Number.isNaN(date.getTime())) return "—"
  const day = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", timeZone }).format(date)
  const time = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit", timeZone }).format(date)
  return `${day}, ${time}`
}

export interface SessionRow {
  readonly id: string
  readonly device: string
  readonly sub: string
  readonly current: boolean
  /**
   * Сеанс поддержки платформы, открытый в этом салоне.
   *
   * СТРОКА ОБЯЗАНА БЫТЬ ВИДНА И НЕ ДОЛЖНА ЗАКРЫВАТЬСЯ КАК СВОЯ. Это не устройство
   * человека: «Завершить» здесь означало бы «закрыть сеанс нашего сотрудника», и кнопка,
   * которая ответит отказом, хуже её отсутствия. Сеанс кончается сам по жёсткому сроку,
   * и срок в строке назван.
   */
  readonly support: boolean
}

/**
 * Строки списка входов.
 *
 * ПОРЯДОК: сеанс поддержки — ПЕРВЫМ, потом текущее устройство, потом остальные.
 * Причина не в вежливости: «кто-то из платформы сейчас внутри моего салона» — самая
 * важная новость этого списка, и найтись она должна сразу, а не на четвёртой строке
 * среди своих телефонов.
 */
export function sessionRows(sessions: readonly CabinetDeviceSessionView[], timeZone?: string): readonly SessionRow[] {
  return sessions
    .toSorted(
      (a, b) =>
        Number(b.support !== null) - Number(a.support !== null) ||
        Number(b.isCurrent) - Number(a.isCurrent),
    )
    .map((session) => {
      const support = session.support
      if (support !== null) {
        /*
         * «ОТ ИМЕНИ ИРИНЫ» ЗДЕСЬ БЫЛО И УБРАНО. Бриф админки (П-Г1) отменяет эту
         * формулировку прямо и называет её неверной: поддержка входит ОТ СВОЕГО ИМЕНИ,
         * отдельной сессией и со своими правами, а не «как Ирина». Строка, говорящая
         * «от имени Ирины», обещает салону обратное — что действия в журнале принадлежат
         * его сотруднице.
         *
         * Остаётся то, что бриф и требует: кто из поддержки, зачем, с какого по какое
         * время и мог ли менять данные.
         */
        return {
          id: session.sessionId,
          device: `Поддержка Planerius · ${support.operatorName}`,
          sub: `${support.reason} · обращение ${support.supportCaseId.slice(0, 8)} · c ${formatMoment(support.startedAt, timeZone)} до ${formatMoment(support.expiresAt, timeZone)} · ${support.readOnly ? "только просмотр" : "с правом изменений"}`,
          current: false,
          support: true,
        }
      }
      return {
        id: session.sessionId,
        device: deviceName(session),
        sub: session.isCurrent
          ? "это устройство · сейчас"
          : `${formatMoment(session.lastUsedAt ?? session.createdAt, timeZone)}${session.ip === null ? "" : ` · ${session.ip}`}`,
        current: session.isCurrent,
        support: false,
      }
    })
}

/** «Изменён 2 июня» — или честное «ещё не менялся». */
export function passwordLine(passwordChangedAt: string | null, timeZone?: string): string {
  if (passwordChangedAt === null) return "Ещё не менялся"
  const date = new Date(passwordChangedAt)
  if (Number.isNaN(date.getTime())) return "Ещё не менялся"
  return `Изменён ${new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", timeZone }).format(date)}`
}

export interface PasswordFormErrors {
  readonly current: string | null
  readonly next: string | null
}

/** Длина — после обрезки пробелов, как на сервере; совпадение со старым сервер отвергнет тоже. */
export function passwordFormErrors(current: string, next: string): PasswordFormErrors {
  return {
    current: current.length === 0 ? "Введите текущий пароль" : null,
    next:
      next.trim().length < 10 ? "Новый пароль короче 10 знаков"
      : next === current ? "Новый пароль совпадает с текущим"
      : null,
  }
}
